import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLiffProfile } from "../../context/LiffProfileContext";
import LoadingBar from "../LoadingBar";

const PRELOAD_ROTATE_DELAY_MS = 10000;

function useHeadPreconnectOnce(url) {
  useEffect(() => {
    try {
      const u = new URL(url);
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = `${u.protocol}//${u.host}`;
      link.crossOrigin = "";
      document.head.appendChild(link);
      return () => document.head.removeChild(link);
    } catch {}
  }, [url]);
}

const BannerSection = ({ sections, onLoaded }) => {
  const { t } = useTranslation();
  const { authToken, userId } = useLiffProfile();

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  // ดึงรายการแบนเนอร์
  useEffect(() => {
    const bannerSection = sections?.find(
      (section) => section.type?.toLowerCase() === "banner"
    );
    if (bannerSection?.items?.length) {
      const originalBanners = bannerSection.items
        .map((item) => ({
          name: item.name,
          image: item.image,
          targetURI: item.targetURI,
        }))
        .filter((b) => !!b.image);
      setBanners(originalBanners);
    } else {
      setBanners([]);
    }
    setLoading(false);
  }, [sections]);

  useEffect(() => {
    if (banners.length > 0) onLoaded?.();
  }, [banners, onLoaded]);

  // Preconnect โดเมนรูป (ช่วยสปีด แม้ไม่แก้ Lighthouse)
  const firstImageUrl = banners[0]?.image || "https://storage.googleapis.com";
  useHeadPreconnectOnce(firstImageUrl);

  // Auto-rotate
  useEffect(() => {
    if (!banners.length) return;
    if (intervalRef.current) clearInterval(intervalRef.current);

    const startTimeout = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 5000);
    }, PRELOAD_ROTATE_DELAY_MS);

    return () => {
      clearTimeout(startTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [banners]);

  const handleDotClick = (index) => {
    setCurrentIndex(index);

    // reset auto-rotate
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, 5000);
  };

  return (
    <div className="relative w-full overflow-hidden flex flex-col items-center">
      {loading ? (
        <LoadingBar loading={true} label="Loading..." />
      ) : banners.length === 0 ? null : (
        <div className="relative w-full h-full overflow-hidden flex justify-center">
          <div
            className="h-full flex transition-transform duration-500"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {banners.map((banner, index) => {
              const isFirst = index === 0;
              return (
                <div
                  key={index}
                  className="w-full h-full flex-shrink-0 relative"
                >
                  <a
                    href={banner.targetURI}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({
                        event: "hero_banner_click",
                        banner_name: banner.name,
                        banner_target: banner.targetURI,
                        banner_index: index,
                      });
                    }}
                  >
                    <img
                      src={banner.image}
                      alt={`Banner: ${banner.name}`}
                      className="w-full h-full object-cover"
                      // ช่วย LCP เฉพาะภาพแรก
                      loading={isFirst ? "eager" : "lazy"}
                      fetchpriority={isFirst ? "high" : "auto"}
                      decoding="async"
                      // ลด CLS: กำหนดสัดส่วน (แก้เป็นสัดส่วนจริงของคุณ)
                      style={{ aspectRatio: "800 / 598" }}
                      width="800"
                      height="598"
                    />
                  </a>
                </div>
              );
            })}
          </div>
          {/* Pagination Dots */}
          <div className="absolute bottom-3 flex space-x-2 z-10">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentIndex === index ? "bg-white scale-110" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerSection;
